import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useOutletContext } from "react-router-dom";
import {
  Paper,
  Stack,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import jsPDF from "jspdf";

import Loader from "../../components/Loader";
import useGetAppPoemSummary from "../../hooks/useGetAppPoemSummary";
import { fetchPoemSurveyAnswers } from "../../hooks/useGetSurveyAnswers";
import { buildPoemFooterText, renderPoemSurveysToPdf } from "../../utils/poemPdfUtils";
import { formatDate } from "../../utils/helpers";
import { VISIT_STATUSES } from "../../utils/constants";

const PoemList = () => {
  const { _id_ } = useParams();
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);

  const userData = JSON.parse(localStorage.getItem("user_data"));
  const role = userData?.role;

  const { infoData, trialFilter, setActive, setBreadcrumbTitle } = useOutletContext();

  setActive("poem");
  setBreadcrumbTitle("POEMs / ePRO / eDiary");

  const { data: summaryData, isLoading } = useGetAppPoemSummary({
    patientId: _id_,
    trialId: trialFilter,
    enabled: !!trialFilter,
  });

  const allowedRoles = [
    "CRO Admin",
    "Investigator",
    "Sub_investigator",
    "Cro_coordinator",
    "Data Manager",
    "Inv_coordinator",
    "Super Admin",
    "Reviewer",
  ];

  const sessions = summaryData?.sessions || [];
  const hasAnySessions = sessions.length > 0;
  const completedSessions = sessions.filter(
    (session) => session.inapp_visit_status === VISIT_STATUSES.COMPLETED
  );

  const routeTo = (id, visitName, inappCompletedDate) => {
    navigate(`/patient/${_id_}/poem/${id}?trial=${trialFilter}`, {
      state: { visitName, inappCompletedDate },
    });
  };

  const handleExportAllPdf = async () => {
    setIsExporting(true);
    try {
      const results = await Promise.all(
        completedSessions.map(async (session) => ({
          visitName: session.visit_name,
          inappCompletedDate: session.inapp_completed_date,
          surveyList: await fetchPoemSurveyAnswers({
            patientId: _id_,
            sessionId: session.session_id,
          }),
        }))
      );

      const nonEmptyResults = results.filter((result) => result.surveyList?.length > 0);
      if (nonEmptyResults.length === 0) return;

      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      nonEmptyResults.forEach(({ visitName, inappCompletedDate, surveyList }, index) => {
        if (index > 0) pdf.addPage();
        const footerText = buildPoemFooterText({
          trialName: infoData?.trial_name,
          visitName,
          siteId: infoData?.site_id,
          participantId: infoData?.participant_id,
          visitDate: inappCompletedDate,
        });
        renderPoemSurveysToPdf(pdf, { surveyList, footerText });
      });

      pdf.save(`POEM_All_${infoData?.participant_id || _id_}.pdf`);
    } finally {
      setIsExporting(false);
    }
  };

  const headerCellSx = {
    backgroundColor: (theme) => theme.palette.grey[100],
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: "0.85rem",
    color: "#424242",
    whiteSpace: "nowrap",
  };

  const bodyCellSx = {
    whiteSpace: "nowrap",
  };

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">POEMs / ePRO / eDiary</Typography>

        {completedSessions.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExportAllPdf}
            disabled={isExporting}
          >
            {isExporting ? "Exporting..." : "Export All as PDF"}
          </Button>
        )}
      </Stack>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "80vh" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {allowedRoles.includes(role) && <TableCell sx={headerCellSx}>Visit Name</TableCell>}

              <TableCell sx={headerCellSx}>Visit Type</TableCell>

              {allowedRoles.includes(role) && (
                <>
                  <TableCell sx={headerCellSx}>Visit Open Date</TableCell>
                  <TableCell sx={headerCellSx}>Visit Close Date</TableCell>
                  <TableCell sx={headerCellSx}>App Start Date</TableCell>
                  <TableCell sx={headerCellSx}>App Completed Date</TableCell>
                  <TableCell sx={headerCellSx}>App Status</TableCell>
                  <TableCell sx={headerCellSx}>Action</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Loader />
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              hasAnySessions &&
              sessions.map((session) => (
                <TableRow
                  key={session.session_id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    routeTo(session.session_id, session.visit_name, session.inapp_completed_date)
                  }
                >
                  {allowedRoles.includes(role) && (
                    <TableCell sx={bodyCellSx}>{session.visit_name}</TableCell>
                  )}

                  <TableCell sx={bodyCellSx}>{session.visit_type || "Regular"}</TableCell>

                  <TableCell sx={bodyCellSx}>{formatDate(session.visit_open_date)}</TableCell>

                  <TableCell sx={bodyCellSx}>{formatDate(session.visit_close_date)}</TableCell>

                  <TableCell sx={bodyCellSx}>{formatDate(session.inapp_start_date)}</TableCell>

                  <TableCell sx={bodyCellSx}>{formatDate(session.inapp_completed_date)}</TableCell>

                  <TableCell sx={bodyCellSx}>{session.inapp_visit_status || "-"}</TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        routeTo(
                          session.session_id,
                          session.visit_name,
                          session.inapp_completed_date
                        );
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}

            {!isLoading && !hasAnySessions && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No visit sessions with app activity found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PoemList;
